import { useState } from 'react';
import { call, type WidgetDef, type WidgetSource } from '../api';
import { libRow, ROW_LIBRARY, ROW_PRESETS, type LibRow } from '../rowLibrary';

/**
 * Rijenbibliotheek in de editor (zoals Xperience): kant-en-klare rijen per thema, met één klik toevoegen,
 * en startpakketten die een hele set rijen tegelijk toevoegen. Rijen die al in de hub staan zijn gemarkeerd.
 */
export function RowLibrary({ existing, room, onAdd, onClose }: {
  existing: WidgetDef[];
  room: number;
  onAdd: (rows: LibRow[], note: string) => void;
  onClose: () => void;
}) {
  const [cat, setCat] = useState(ROW_LIBRARY[0].id);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<{ ok: boolean; chips: string[]; title: string; source?: WidgetSource; total?: number } | null>(null);
  const describe = async () => {
    if (text.trim().length < 4) return;
    setBusy(true);
    setFound(await call<{ ok: boolean; chips: string[]; title: string; source?: WidgetSource; total?: number }>('/api/widgets/describe', { method: 'POST', body: JSON.stringify({ text }) }).catch(() => null));
    setBusy(false);
  };
  const has = (r: LibRow) => existing.some((w) => w.title === r.title);
  const category = ROW_LIBRARY.find((c) => c.id === cat) ?? ROW_LIBRARY[0];

  const addPreset = (rows: string[], name: string) => {
    const list = rows.map(libRow).filter((r): r is LibRow => !!r && !has(r)).slice(0, room);
    if (!list.length) return onAdd([], room <= 0 ? 'Deze hub zit vol (maximaal 30 rijen).' : `Alle rijen van "${name}" staan er al.`);
    onAdd(list, `"${name}": ${list.length} ${list.length === 1 ? 'rij' : 'rijen'} toegevoegd.`);
  };

  return (
    <div className="rl" role="region" aria-label="Rijenbibliotheek">
      <div className="rl-head">
        <strong>Rijenbibliotheek</strong>
        <span className="subtle">Kant-en-klare rijen via TMDB. Na toevoegen kun je ze rechts nog aanpassen.</span>
        <button className="icon-mini" aria-label="Sluiten" onClick={onClose}>✕</button>
      </div>

      <div className="rl-label">Zelf beschrijven</div>
      <form className="bk-form" onSubmit={(e) => { e.preventDefault(); void describe(); }}>
        <input className="text-input" value={text} onChange={(e) => { setText(e.target.value); setFound(null); }} placeholder="bijv. spannende films uit de jaren 90 met een twist" maxLength={160} />
        <button className="btn btn-sm" type="submit" disabled={busy || text.trim().length < 4}>{busy ? 'Even denken…' : 'Maak rij'}</button>
      </form>
      {found && (found.ok && found.source ? (
        <div className="rl-found">
          <span className="qs-chips">{found.chips.map((c) => <span key={c} className="chip">{c}</span>)}</span>
          <span className="subtle">{found.total ? `${found.total.toLocaleString('nl-NL')} titels` : 'Nog geen titels gevonden'}</span>
          <button className="btn btn-primary btn-sm" disabled={room <= 0 || !found.total} onClick={() => { onAdd([{ id: `ai-${Date.now()}`, title: found.title, source: found.source! }], `"${found.title}" toegevoegd.`); setText(''); setFound(null); }}>+ Toevoegen</button>
        </div>
      ) : (
        <p className="subtle">Dat begreep ik niet goed genoeg. Noem bijvoorbeeld een genre, een periode, een land, een acteur of een thema (twist, zombies, tijdreizen, waargebeurd…).</p>
      ))}

      <div className="rl-label">Startpakketten</div>
      <div className="rl-presets">
        {ROW_PRESETS.map((p) => (
          <button key={p.id} className="rl-preset" disabled={room <= 0} onClick={() => addPreset(p.rows, p.title)}>
            <strong>{p.title}</strong>
            <span className="subtle">{p.hint}</span>
          </button>
        ))}
      </div>

      <div className="rl-label">Losse rijen</div>
      <div className="qs-chips rl-cats" role="tablist">
        {ROW_LIBRARY.map((c) => (
          <button key={c.id} role="tab" className="chip" aria-selected={c.id === cat} aria-pressed={c.id === cat} onClick={() => setCat(c.id)}>{c.title}</button>
        ))}
      </div>
      <ul className="rl-rows">
        {category.rows.map((r) => {
          const added = has(r);
          return (
            <li key={r.id}>
              <span>{r.title}</span>
              <button className="btn btn-sm" disabled={added || room <= 0} onClick={() => onAdd([r], `"${r.title}" toegevoegd.`)}>{added ? 'Staat erin' : '+ Toevoegen'}</button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
